import { useState, useEffect, useCallback } from 'react';
import { Empleado, PeriodoVacaciones, BalanceEmpleado, ResumenDepartamento, Departamento, TipoLicencia } from '../tipos/vacaciones';
import * as empleadoService from '../services/empleadoService';
import * as periodoService from '../services/periodoService';
import * as balanceService from '../services/balanceService';
import * as departamentoService from '../services/departamentoService';
import * as tipoLicenciaService from '../services/tipoLicenciaService';

interface DashboardData {
  empleados: Empleado[];
  periodos: PeriodoVacaciones[];
  balances: BalanceEmpleado[];
  resumenDepartamentos: ResumenDepartamento[];
  departamentos: Departamento[];
  tiposLicencia: TipoLicencia[];
}

interface UseDashboardDataResult {
  loading: boolean;
  error: string | null;
  data: DashboardData;
  retry: () => void;
}

const ANIO_ACTUAL = new Date().getFullYear();

const datosVacios: DashboardData = {
  empleados: [],
  periodos: [],
  balances: [],
  resumenDepartamentos: [],
  departamentos: [],
  tiposLicencia: [],
};

/**
 * Hook que orquesta la carga de todos los datos del dashboard.
 * Carga empleados, periodos, balances, resumen de departamentos,
 * departamentos y tipos de licencia en paralelo.
 */
export function useDashboardData(): UseDashboardDataResult {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData>(datosVacios);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [empleados, periodos, balances, resumenDepartamentos, departamentos, tiposLicencia] = await Promise.all([
        empleadoService.getAll(),
        periodoService.getAll(ANIO_ACTUAL),
        balanceService.getAllByAnio(ANIO_ACTUAL),
        departamentoService.getResumen(),
        departamentoService.getAll(),
        tipoLicenciaService.getAll(),
      ]);

      setData({ empleados, periodos, balances, resumenDepartamentos, departamentos, tiposLicencia });
    } catch (err) {
      const mensaje =
        err instanceof Error
          ? err.message
          : 'Error desconocido al cargar los datos';
      setError(mensaje);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  return {
    loading,
    error,
    data,
    retry: cargarDatos,
  };
}
